import { Navigate, Route } from 'react-router-dom';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { isUndefined } from 'lodash';
import { routes } from '../../utils/helpers/ApiClient';
import CMSWrapper from '../CMSWrapper';
import {
  PhdDashboardPage,
  PhdStudentsPage,
  PhdStudentsEdit,
  PhdSupervisorsPage,
  PhdSupervisorsEdit,
  PhdIndividualPlansPage,
  PhdIndividualPlansEdit,
  PhdStudentEvaluationsPage,
  PhdStudentEvaluationsEdit,
  PhdStudentEvaluationGradesPage,
  PhdStudentEvaluationGradesEdit,
  PhdApplicationsPage,
  PhdApplicationsCreatePage,
  PhdDissertationExamApplicationsEdit,
  PhdExaminerProposalsPage,
  PhdDissertationExamExaminerProposalsEdit,
  PhdDissertationDefenseApplicationsEdit,
  PhdDissertationDefenseExaminerProposalsEdit,
  PhdSubjectsExamApplicationsEdit,
  PhdThesesPage,
  PhdThesesEdit,
  PhdThesesDefend,
  PhdThesesManualDefense,
  PhdThesisDetail,
  PhdCommentsEdit,
  PhdStudentPlanPage,
  PhdSettingsPage,
  PhdStudyFieldsPage,
  PhdStudyFieldsEdit,
  PhdWorkingGroupsPage,
  PhdWorkingGroupsEdit,
  PhdStudySubjectsEdit,
} from '../pages/CMSPages';

const CmsAnyPermission = ({ permissions, children }) => {
  const user = useSelector((state) => state.auth.user);

  const hasAnyPermission = useMemo(() => {
    if (isUndefined(user)) {
      return false;
    }

    if (!Array.isArray(permissions) || permissions.length === 0) {
      return true;
    }

    return permissions.some((permission) =>
      Object.prototype.hasOwnProperty.call(user?.permissions || {}, permission),
    );
  }, [permissions, user]);

  return (
    <CMSWrapper permissions={hasAnyPermission ? undefined : ['__forbidden__']}>
      {children}
    </CMSWrapper>
  );
};

const PhdRoutes = () => (
  <>
    <Route
      path={`${routes.cmsPhdDashboard}`}
      element={
        <CMSWrapper permissions={['phd-dashboard.view']}>
          <PhdDashboardPage />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdStudents}`}
      element={
        <CMSWrapper permissions={['phd-students.index']}>
          <PhdStudentsPage />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdSupervisors}`}
      element={
        <CmsAnyPermission permissions={['phd-supervisors.index']}>
          <PhdSupervisorsPage />
        </CmsAnyPermission>
      }
    />
    <Route
      path={`${routes.cmsPhdSupervisorsCreate}`}
      element={
        <CMSWrapper permissions={['phd-supervisors.store']}>
          <PhdSupervisorsEdit mode={'create'} />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdSupervisorsEdit}/:id`}
      element={
        <CMSWrapper permissions={['phd-supervisors.update']}>
          <PhdSupervisorsEdit mode={'edit'} />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdStudentPlan}`}
      element={
        <CMSWrapper>
          <PhdStudentPlanPage />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdStudentsEdit}/:id`}
      element={
        <CMSWrapper permissions={['phd-students.update']}>
          <PhdStudentsEdit mode={'edit'} />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdStudentsCreate}`}
      element={
        <CMSWrapper permissions={['phd-students.store']}>
          <PhdStudentsEdit mode={'create'} />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdIndividualPlans}`}
      element={
        <CMSWrapper permissions={['phd-individual-plans.index']}>
          <PhdIndividualPlansPage />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdIndividualPlansEdit}/:id`}
      element={
        <CMSWrapper permissions={['phd-individual-plans.update']}>
          <PhdIndividualPlansEdit mode={'edit'} />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdIndividualPlansCreate}`}
      element={
        <CMSWrapper permissions={['phd-individual-plans.store']}>
          <PhdIndividualPlansEdit mode={'create'} />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdStudentEvaluations}`}
      element={
        <CMSWrapper permissions={['phd-student-evaluations.index']}>
          <PhdStudentEvaluationsPage />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdStudentEvaluationsEdit}/:id`}
      element={
        <CMSWrapper permissions={['phd-student-evaluations.update']}>
          <PhdStudentEvaluationsEdit mode={'edit'} />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdStudentEvaluationsCreate}`}
      element={
        <CMSWrapper permissions={['phd-student-evaluations.store']}>
          <PhdStudentEvaluationsEdit mode={'create'} />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdStudentEvaluationGrades}`}
      element={
        <CMSWrapper permissions={['phd-student-evaluation-grades.index']}>
          <PhdStudentEvaluationGradesPage />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdStudentEvaluationGradesEdit}/:id`}
      element={
        <CMSWrapper permissions={['phd-student-evaluation-grades.update']}>
          <PhdStudentEvaluationGradesEdit mode={'edit'} />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdStudentEvaluationGradesCreate}`}
      element={
        <CMSWrapper permissions={['phd-student-evaluation-grades.store']}>
          <PhdStudentEvaluationGradesEdit mode={'create'} />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdApplications}`}
      element={
        <CmsAnyPermission
          permissions={['phd-applications.index', 'phd-student-plan.index']}
        >
          <PhdApplicationsPage initialTab="dissertationExam" />
        </CmsAnyPermission>
      }
    />
    <Route
      path={`${routes.cmsPhdMyApplications}`}
      element={
        <CMSWrapper permissions={['phd-student-plan.index']}>
          <PhdApplicationsPage initialTab="dissertationExam" />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdDissertationExamApplications}`}
      element={
        <CmsAnyPermission
          permissions={['phd-applications.index', 'phd-student-plan.index']}
        >
          <PhdApplicationsPage initialTab="dissertationExam" />
        </CmsAnyPermission>
      }
    />
    <Route
      path={`${routes.cmsPhdDissertationDefenseApplications}`}
      element={
        <CmsAnyPermission
          permissions={['phd-applications.index', 'phd-student-plan.index']}
        >
          <PhdApplicationsPage initialTab="dissertationDefense" />
        </CmsAnyPermission>
      }
    />
    <Route
      path={`${routes.cmsPhdSubjectsExamApplications}`}
      element={
        <CmsAnyPermission
          permissions={['phd-applications.index', 'phd-student-plan.index']}
        >
          <PhdApplicationsPage initialTab="subjectsExam" />
        </CmsAnyPermission>
      }
    />
    <Route
      path={`${routes.cmsPhdApplicationsCreate}`}
      element={
        <CmsAnyPermission
          permissions={[
            'phd-dissertation-exam-applications.store',
            'phd-dissertation-defense-applications.store',
            'phd-subjects-exam-applications.store',
            'phd-student-plan.index',
          ]}
        >
          <PhdApplicationsCreatePage />
        </CmsAnyPermission>
      }
    />
    <Route
      path={`${routes.cmsPhdDissertationExamApplicationsEdit}/:id`}
      element={
        <CmsAnyPermission
          permissions={[
            'phd-dissertation-exam-applications.update',
            'phd-student-plan.index',
          ]}
        >
          <PhdDissertationExamApplicationsEdit mode={'edit'} />
        </CmsAnyPermission>
      }
    />
    <Route
      path={`${routes.cmsPhdDissertationExamApplicationsCreate}`}
      element={
        <CmsAnyPermission
          permissions={[
            'phd-dissertation-exam-applications.store',
            'phd-student-plan.index',
          ]}
        >
          <Navigate
            to={`${routes.cmsPhdApplicationsCreate}?type=dissertationExam`}
            replace
          />
        </CmsAnyPermission>
      }
    />
    <Route
      path={`${routes.cmsPhdExaminerProposals}`}
      element={
        <CMSWrapper permissions={['phd-examiner-proposals.index']}>
          <PhdExaminerProposalsPage initialTab="exam" />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdDissertationExamExaminerProposals}`}
      element={
        <CMSWrapper permissions={['phd-examiner-proposals.index']}>
          <PhdExaminerProposalsPage initialTab="exam" />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdDissertationDefenseExaminerProposals}`}
      element={
        <CMSWrapper permissions={['phd-examiner-proposals.index']}>
          <PhdExaminerProposalsPage initialTab="defense" />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdDissertationExamExaminerProposalsEdit}/:id`}
      element={
        <CMSWrapper
          permissions={['phd-dissertation-exam-examiner-proposals.update']}
        >
          <PhdDissertationExamExaminerProposalsEdit mode={'edit'} />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdDissertationExamExaminerProposalsCreate}`}
      element={
        <CMSWrapper
          permissions={['phd-dissertation-exam-examiner-proposals.store']}
        >
          <PhdDissertationExamExaminerProposalsEdit mode={'create'} />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdDissertationDefenseApplicationsEdit}/:id`}
      element={
        <CmsAnyPermission
          permissions={[
            'phd-dissertation-defense-applications.update',
            'phd-student-plan.index',
          ]}
        >
          <PhdDissertationDefenseApplicationsEdit mode={'edit'} />
        </CmsAnyPermission>
      }
    />
    <Route
      path={`${routes.cmsPhdDissertationDefenseApplicationsCreate}`}
      element={
        <CmsAnyPermission
          permissions={[
            'phd-dissertation-defense-applications.store',
            'phd-student-plan.index',
          ]}
        >
          <Navigate
            to={`${routes.cmsPhdApplicationsCreate}?type=dissertationDefense`}
            replace
          />
        </CmsAnyPermission>
      }
    />
    <Route
      path={`${routes.cmsPhdDissertationDefenseExaminerProposalsEdit}/:id`}
      element={
        <CMSWrapper
          permissions={['phd-dissertation-defense-examiner-proposals.update']}
        >
          <PhdDissertationDefenseExaminerProposalsEdit mode={'edit'} />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdDissertationDefenseExaminerProposalsCreate}`}
      element={
        <CMSWrapper
          permissions={['phd-dissertation-defense-examiner-proposals.store']}
        >
          <PhdDissertationDefenseExaminerProposalsEdit mode={'create'} />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdSubjectsExamApplicationsEdit}/:id`}
      element={
        <CmsAnyPermission
          permissions={[
            'phd-subjects-exam-applications.update',
            'phd-student-plan.index',
          ]}
        >
          <PhdSubjectsExamApplicationsEdit mode={'edit'} />
        </CmsAnyPermission>
      }
    />
    <Route
      path={`${routes.cmsPhdSubjectsExamApplicationsCreate}`}
      element={
        <CmsAnyPermission
          permissions={[
            'phd-subjects-exam-applications.store',
            'phd-student-plan.index',
          ]}
        >
          <Navigate
            to={`${routes.cmsPhdApplicationsCreate}?type=subjectsExam`}
            replace
          />
        </CmsAnyPermission>
      }
    />
    <Route
      path={`${routes.cmsPhdTheses}`}
      element={
        <CMSWrapper permissions={['phd-theses.index']}>
          <PhdThesesPage />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdThesesEdit}/:id`}
      element={
        <CMSWrapper permissions={['phd-theses.update']}>
          <PhdThesesEdit mode={'edit'} />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdThesesCreate}`}
      element={
        <CMSWrapper permissions={['phd-theses.store']}>
          <PhdThesesEdit mode={'create'} />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdThesesDefend}/:id`}
      element={
        <CMSWrapper permissions={['phd-theses.update']}>
          <PhdThesesDefend />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdThesesManualDefense}`}
      element={
        <CMSWrapper permissions={['phd-theses.store']}>
          <PhdThesesManualDefense />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdThesesDetail}/:id`}
      element={
        <CMSWrapper permissions={['phd-theses.show']}>
          <PhdThesisDetail />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdSystemState}`}
      element={<Navigate to={routes.cmsPhdSettings} replace />}
    />
    <Route
      path={`${routes.cmsPhdSettings}`}
      element={
        <CMSWrapper permissions={['phd-system-states.store']}>
          <PhdSettingsPage />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdCommentsEdit}/:id`}
      element={
        <CMSWrapper permissions={['phd-comments.update']}>
          <PhdCommentsEdit mode={'edit'} />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdCommentsCreate}`}
      element={
        <CMSWrapper permissions={['phd-comments.store']}>
          <PhdCommentsEdit mode={'create'} />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdStudyFields}`}
      element={
        <CmsAnyPermission
          permissions={['phd-study-fields.index', 'phd-study-subjects.index']}
        >
          <PhdStudyFieldsPage />
        </CmsAnyPermission>
      }
    />
    <Route
      path={`${routes.cmsPhdStudyFieldsEdit}/:id`}
      element={
        <CMSWrapper permissions={['phd-study-fields.update']}>
          <PhdStudyFieldsEdit mode={'edit'} />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdStudyFieldsCreate}`}
      element={
        <CMSWrapper permissions={['phd-study-fields.store']}>
          <PhdStudyFieldsEdit mode={'create'} />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdWorkingGroups}`}
      element={
        <CMSWrapper permissions={['phd-working-groups.index']}>
          <PhdWorkingGroupsPage />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdWorkingGroupsEdit}/:id`}
      element={
        <CmsAnyPermission permissions={['phd-working-groups.store', 'phd-working-groups.update']}>
          <PhdWorkingGroupsEdit />
        </CmsAnyPermission>
      }
    />
    <Route
      path={`${routes.cmsPhdStudySubjectsEdit}/:id`}
      element={
        <CMSWrapper permissions={['phd-study-subjects.update']}>
          <PhdStudySubjectsEdit mode={'edit'} />
        </CMSWrapper>
      }
    />
    <Route
      path={`${routes.cmsPhdStudySubjectsCreate}`}
      element={
        <CMSWrapper permissions={['phd-study-subjects.store']}>
          <PhdStudySubjectsEdit mode={'create'} />
        </CMSWrapper>
      }
    />
  </>
);

export default PhdRoutes;
